import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { getClientBuildCommit } from "@/lib/buildIdentity";
import {
  applicationAreaFromRoute,
  buildStaffForegroundIssueNote,
  redactStaffRoute,
  singaporeTimestamp,
} from "@shared/staffForegroundIssue";
import { foregroundLoading } from "@/lib/foregroundLoading";
import {
  getForegroundRetrySnapshot,
  subscribeForegroundRetry,
} from "@/lib/foregroundRetry";

function contextualLabel(pathname: string): string {
  if (pathname.startsWith("/sales/catalogue")) return "Loading catalogue…";
  if (/^\/sales\/proposals\/\d+/.test(pathname)) return "Loading proposal details…";
  if (pathname.startsWith("/sales/proposals")) return "Loading proposals…";
  if (pathname.startsWith("/site-visits") || pathname.startsWith("/repair-orders")) {
    return "Loading service records…";
  }
  if (pathname === "/sales" || pathname.startsWith("/sales/insights")) return "Loading sales dashboard…";
  return "Checking the latest records…";
}

function makeIncidentId() {
  return `fg-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

type StaffReportMutation = {
  mutate: (input: Record<string, unknown>) => void;
  isPending: boolean;
  data?: { reportUuid?: string } | null;
};

function GlobalLoadingBarView({
  staffReport,
}: {
  staffReport: StaffReportMutation | null;
}) {
  const snapshot = useSyncExternalStore(
    foregroundLoading.subscribe,
    foregroundLoading.getSnapshot,
    foregroundLoading.getSnapshot,
  );
  const retryAction = useSyncExternalStore(
    subscribeForegroundRetry,
    getForegroundRetrySnapshot,
    getForegroundRetrySnapshot,
  );
  const [location] = useLocation();
  const [clock, setClock] = useState(() => Date.now());
  const [showSlowActions, setShowSlowActions] = useState(false);
  const [incidentId, setIncidentId] = useState(makeIncidentId);

  const safeRoute = useMemo(() => {
    const query = typeof window === "undefined" ? "" : window.location.search;
    return redactStaffRoute(`${location}${query}`);
  }, [location]);

  useEffect(() => {
    if (!snapshot.visible) {
      setShowSlowActions(false);
      return;
    }
    const timer = window.setInterval(() => setClock(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [snapshot.visible]);

  useEffect(() => {
    if (snapshot.activeSinceMs == null) return;
    setIncidentId(makeIncidentId());
  }, [snapshot.activeSinceMs, snapshot.generation]);

  if (!snapshot.visible) return null;
  const elapsedMs =
    snapshot.activeSinceMs == null
      ? 0
      : Math.max(0, clock - snapshot.activeSinceMs);
  const showContext = elapsedMs >= 2_000;
  const showStillLoading = elapsedMs >= 5_000;
  const showTooLong = elapsedMs >= 15_000;
  const valuetext =
    snapshot.progress >= 100
      ? "Loading complete"
      : snapshot.activeOperation
        ? `Loading ${snapshot.activeOperation}`
        : `Loading ${Math.round(snapshot.progress)} percent`;

  return (
    <div className="fixed inset-x-0 top-0 z-[9999]" data-testid="global-loading-layer">
      <div
        className="pointer-events-none h-[3px] overflow-hidden bg-transparent"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={snapshot.progress}
        aria-valuetext={valuetext}
        data-testid="global-loading-bar"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-[#1a2744]/15"
          data-testid="global-loading-bar-track"
        />
        <div
          className="relative h-full bg-[#1a2744] shadow-[0_0_8px_rgba(26,39,68,0.45)] motion-reduce:transition-none"
          style={{ width: `${snapshot.progress}%`, transition: "width 160ms ease-out" }}
          data-testid="global-loading-bar-indicator"
        />
      </div>
      {showContext ? (
        <div className="pointer-events-none mt-1.5 px-3 text-[11px] text-[#172442]/75">
          <p>{contextualLabel(location)}</p>
          {showStillLoading ? (
            <p className="text-[#172442]/60">Still loading the latest information…</p>
          ) : null}
        </div>
      ) : null}
      {showTooLong ? (
        <div className="mt-1.5 px-3">
          <button
            type="button"
            className="rounded border border-[#172442]/20 bg-white/90 px-2 py-1 text-[11px] text-[#172442]"
            onClick={() => setShowSlowActions((v) => !v)}
          >
            Taking too long?
          </button>
          {showSlowActions ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[#172442]/15 bg-white/95 p-2 text-[11px] text-[#172442]/85">
              {retryAction ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => {
                    foregroundLoading.beginSession();
                    void retryAction.run();
                  }}
                >
                  {retryAction.label}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </Button>
              )}
              {staffReport ? (
                <StaffForegroundIssueReportButton
                  report={staffReport}
                  elapsedMs={elapsedMs}
                  incidentId={incidentId}
                  safeRoute={safeRoute}
                  operation={snapshot.activeOperation}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StaffForegroundIssueReportButton({
  report,
  elapsedMs,
  incidentId,
  safeRoute,
  operation,
}: {
  report: StaffReportMutation;
  elapsedMs: number;
  incidentId: string;
  safeRoute: string;
  operation: string | null;
}) {
  const { user } = useAuth({ queryEnabled: true });
  const op = operation?.trim() || "global_foreground_loading_timeout";
  return (
    <>
      <Button
        type="button"
        size="sm"
        className="h-7 bg-[#172442] px-2 text-[11px] text-white hover:bg-[#172442]/90"
        disabled={report.isPending || Boolean(report.data?.reportUuid)}
        onClick={() => {
          report.mutate({
            route: safeRoute,
            operation: op.slice(0, 128),
            safeErrorCode: "TIMEOUT",
            note: buildStaffForegroundIssueNote({
              incidentId,
              staffName: user?.name ?? user?.email ?? null,
              area: applicationAreaFromRoute(safeRoute),
              operation: op.slice(0, 128),
              singaporeAt: singaporeTimestamp(),
              navigationContext: safeRoute,
              retryAttempted: false,
              stackSnippet: `elapsedMs=${elapsedMs}; operation=${op}; build=${getClientBuildCommit()}; role=${String(user?.role ?? "unknown")}`,
            }),
            clientIdempotencyKey: incidentId.slice(0, 64),
            retryAttempted: false,
          });
        }}
      >
        {report.isPending ? "Sending…" : "Send Error Report"}
      </Button>
      {report.data?.reportUuid ? (
        <span className="text-emerald-700">Sent ({report.data.reportUuid})</span>
      ) : null}
    </>
  );
}

function StaffPortalGlobalLoadingBar() {
  const report = trpc.system.reportStaffForegroundIssue.useMutation({ meta: { background: true } });
  return <GlobalLoadingBarView staffReport={report} />;
}

function CustomerSafeGlobalLoadingBar() {
  return <GlobalLoadingBarView staffReport={null} />;
}

export function GlobalLoadingBar({ includeStaffAuth = true }: { includeStaffAuth?: boolean }) {
  if (includeStaffAuth) return <StaffPortalGlobalLoadingBar />;
  return <CustomerSafeGlobalLoadingBar />;
}
